import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getProduttori, getStoricoCliente, getMezzi } from "../lib/api";
import { Card } from "../components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { formatCurrency, formatDateIt, formatNumber } from "../lib/constants";
import { Calendar, Users, TrendingUp } from "lucide-react";

export default function StoricoCliente() {
  const [searchParams] = useSearchParams();
  const [produttori, setProduttori] = useState([]);
  const [mezzi, setMezzi] = useState([]);
  const [pid, setPid] = useState(searchParams.get("pid") || "");
  const [storico, setStorico] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([getProduttori(), getMezzi()]).then(([p, m]) => { setProduttori(p); setMezzi(m); });
  }, []);

  const mezziById = useMemo(() => Object.fromEntries(mezzi.map((m) => [m.id, m])), [mezzi]);

  useEffect(() => {
    if (!pid) { setStorico(null); return; }
    setLoading(true);
    getStoricoCliente(pid).then(setStorico).finally(() => setLoading(false));
  }, [pid]);

  return (
    <div className="space-y-4" data-testid="storico-cliente-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Storico cliente</h1>
        <p className="text-slate-500 text-sm mt-1">Tutti i ritiri di un produttore con progressivi cumulati</p>
      </div>

      <Card className="p-4 bg-white border border-slate-200">
        <Select value={pid} onValueChange={setPid}>
          <SelectTrigger data-testid="storico-prod-select"><SelectValue placeholder="Seleziona un produttore…" /></SelectTrigger>
          <SelectContent>
            {produttori.map((p) => <SelectItem key={p.id} value={p.id}>{p.ragioneSociale} — {p.zona}</SelectItem>)}
          </SelectContent>
        </Select>
      </Card>

      {!pid && (
        <Card className="p-8 text-center text-slate-500" data-testid="storico-empty">
          <Users size={36} className="mx-auto text-slate-300 mb-2" />
          Seleziona un produttore per vedere lo storico completo
        </Card>
      )}

      {loading && <div className="p-4 text-slate-500">Caricamento…</div>}

      {storico && (
        <>
          <Card className="p-5 bg-white border border-slate-200" data-testid="storico-totale">
            <div className="font-semibold text-lg text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{storico.produttore.ragioneSociale}</div>
            <div className="text-xs text-slate-500 mb-3">{storico.produttore.zona} · {storico.produttore.indirizzo}</div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <div className="text-[10px] uppercase font-semibold text-slate-500">Totale ritiri</div>
                <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{formatNumber(storico.totale.ritiri)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-semibold text-slate-500">Kg totali</div>
                <div className="text-2xl font-bold text-[#F57C00]" style={{ fontFamily: "Outfit, sans-serif" }}>{formatNumber(storico.totale.kg)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-semibold text-slate-500">Importo cumulato</div>
                <div className="text-2xl font-bold text-[#1B5E20]" style={{ fontFamily: "Outfit, sans-serif" }}>{formatCurrency(storico.totale.importo)}</div>
              </div>
            </div>
            {storico.totale.primoRitiro && (
              <div className="text-xs text-slate-500 mt-3 flex items-center gap-3">
                <span className="flex items-center gap-1"><Calendar size={11} /> Primo: {formatDateIt(storico.totale.primoRitiro)}</span>
                <span className="flex items-center gap-1"><TrendingUp size={11} /> Ultimo: {formatDateIt(storico.totale.ultimoRitiro)}</span>
              </div>
            )}
          </Card>

          {storico.ritiri.length === 0 ? (
            <Card className="p-6 text-center text-slate-500">Nessun ritiro registrato</Card>
          ) : (
            <Card className="bg-white border border-slate-200 overflow-hidden" data-testid="storico-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-center px-2 py-2 w-10">N°</th>
                      <th className="text-left px-3 py-2">Data</th>
                      <th className="text-right px-3 py-2">Kg</th>
                      <th className="text-right px-3 py-2">€/kg</th>
                      <th className="text-right px-3 py-2">Importo</th>
                      <th className="text-right px-3 py-2 bg-[#F57C00]/10 text-[#F57C00]">Prog. kg</th>
                      <th className="text-right px-3 py-2 bg-[#1B5E20]/10 text-[#1B5E20]">Prog. €</th>
                      <th className="text-left px-3 py-2">Mezzo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {storico.ritiri.map((r, idx) => {
                      const mez = r.mezzoId ? mezziById[r.mezzoId] : null;
                      const num = storico.ritiri.length - idx;
                      return (
                        <tr key={r.id} className="border-t border-slate-100" data-testid={`storico-row-${r.id}`}>
                          <td className="text-center px-2 py-2 font-bold text-slate-400" style={{ fontFamily: "Outfit, sans-serif" }}>#{num}</td>
                          <td className="px-3 py-2">{formatDateIt(r.data)}</td>
                          <td className="text-right px-3 py-2 font-medium">{formatNumber(r.quantitaKg)}</td>
                          <td className="text-right px-3 py-2 text-slate-500">{r.prezzoUnitario ? r.prezzoUnitario.toFixed(2) : "-"}</td>
                          <td className="text-right px-3 py-2 font-medium">{formatCurrency(r.importo)}</td>
                          <td className="text-right px-3 py-2 font-bold text-[#F57C00] bg-[#F57C00]/5">{formatNumber(r.progKg)}</td>
                          <td className="text-right px-3 py-2 font-bold text-[#1B5E20] bg-[#1B5E20]/5">{formatCurrency(r.progImporto)}</td>
                          <td className="px-3 py-2">
                            {mez ? (
                              <span className="inline-flex items-center gap-1 text-xs">
                                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: mez.colore }} />
                                <span className="text-slate-700 truncate">{mez.nome.replace(/^Mezzo \d+ - /, "")}</span>
                              </span>
                            ) : <span className="text-slate-300 text-xs">-</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
